'use client';

/**
 * Ambient laboratory sound. Fully synthesized — no audio files.
 *
 * OFF by default; starts only when the visitor toggles useWorld.audioOn.
 * Two detuned low oscillators through a lowpass form a quiet bed, a very
 * slow LFO breathes the level, and a gentle filtered impulse sounds every
 * 6–14 seconds. Master level stays at or below 0.05. Everything ramps down
 * and the context closes cleanly on toggle-off or unmount.
 */

import { useEffect } from 'react';
import { useWorld } from '../lab/store';

const BED_LEVEL = 0.038;
const LFO_DEPTH = 0.01; // BED_LEVEL + LFO_DEPTH stays ≤ 0.05

export default function LabAudio() {
  const audioOn = useWorld((s) => s.audioOn);

  useEffect(() => {
    if (!audioOn || typeof window === 'undefined') return undefined;
    const AudioCtx =
      window.AudioContext ??
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AudioCtx === undefined) return undefined;

    let ctx: AudioContext;
    try {
      ctx = new AudioCtx();
    } catch {
      return undefined;
    }
    void ctx.resume().catch(() => undefined);

    let disposed = false;
    let impulseTimer: number | undefined;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, ctx.currentTime);
    master.gain.exponentialRampToValueAtTime(BED_LEVEL, ctx.currentTime + 2.5);
    master.connect(ctx.destination);

    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 240;
    lowpass.Q.value = 0.7;
    lowpass.connect(master);

    const oscA = ctx.createOscillator();
    oscA.type = 'sine';
    oscA.frequency.value = 54;
    const gainA = ctx.createGain();
    gainA.gain.value = 0.5;
    oscA.connect(gainA);
    gainA.connect(lowpass);

    const oscB = ctx.createOscillator();
    oscB.type = 'triangle';
    oscB.frequency.value = 81.4;
    oscB.detune.value = 6;
    const gainB = ctx.createGain();
    gainB.gain.value = 0.28;
    oscB.connect(gainB);
    gainB.connect(lowpass);

    // Slow breathing of the bed level.
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.05;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = LFO_DEPTH;
    lfo.connect(lfoGain);
    lfoGain.connect(master.gain);

    oscA.start();
    oscB.start();
    lfo.start();

    const schedule = () => {
      impulseTimer = window.setTimeout(impulse, 6000 + Math.random() * 8000);
    };

    // A soft, distant instrument reading — a single filtered sine swell.
    const impulse = () => {
      if (disposed) return;
      const t0 = ctx.currentTime;
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      const freq = 260 + Math.random() * 620;
      osc.frequency.value = freq;
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.value = freq;
      band.Q.value = 9;
      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, t0);
      env.gain.exponentialRampToValueAtTime(0.02, t0 + 0.18);
      env.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.4);
      osc.connect(band);
      band.connect(env);
      env.connect(master);
      osc.start(t0);
      osc.stop(t0 + 1.5);
      osc.onended = () => {
        osc.disconnect();
        band.disconnect();
        env.disconnect();
      };
      schedule();
    };
    schedule();

    return () => {
      disposed = true;
      if (impulseTimer !== undefined) window.clearTimeout(impulseTimer);
      const t = ctx.currentTime;
      try {
        master.gain.cancelScheduledValues(t);
        master.gain.setValueAtTime(Math.max(master.gain.value, 0.0001), t);
        master.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      } catch {
        // context may already be closing
      }
      window.setTimeout(() => {
        void ctx.close().catch(() => undefined);
      }, 450);
    };
  }, [audioOn]);

  return null;
}
